import { useEffect, useRef } from 'react';
import enviro from 'enviro';
import I18n from 'I18n';
import { openPopup, openTab } from '../../utils/popupUtils';
import { getAbsoluteReconnectUrl } from '../../utils/urlUtils';
import { usePopupUrl } from '../../hooks/usePopupUrl';
const FIVE_MINUTES = 300000;
const DELAY_500_MS = 500;
export const URLServiceComponent = ({
  metadata,
  onAuthCancelled,
  onAuthFailed,
  onAuthSucceeded,
  reconnectStarted,
  url: urlTemplate,
  timeout = FIVE_MINUTES
}) => {
  const appId = metadata === null || metadata === void 0 ? void 0 : metadata.appId;
  const accountId = metadata === null || metadata === void 0 ? void 0 : metadata.accountId;
  const businessUnitId = metadata === null || metadata === void 0 ? void 0 : metadata.businessUnitId;
  const reconnectUrl = getAbsoluteReconnectUrl({
    urlTemplate,
    appId,
    accountId,
    businessUnitId
  });
  const {
    popupUrl,
    error: popupUrlError
  } = usePopupUrl({
    url: reconnectUrl,
    skip: !reconnectStarted
  });
  const popupRef = useRef(null);
  const authStartedRef = useRef(false);
  useEffect(() => {
    if (popupUrlError) {
      onAuthFailed === null || onAuthFailed === void 0 || onAuthFailed({
        error: popupUrlError,
        errorMessage: I18n.text('connectionStatusAlerts.fetchAuthorizeUrlError.message')
      });
    }
  }, [popupUrlError, onAuthFailed]);
  useEffect(() => {
    const openPopupWindow = popupWindowUrl => {
      let timeoutId = 0;
      let intervalId = 0;
      const clearTimers = () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
      const handleAuthCancelled = ({
        message
      }) => {
        clearTimers();
        const messageText = message || I18n.text('connectionStatusAlerts.cancelReconnectionError.message');
        onAuthCancelled === null || onAuthCancelled === void 0 || onAuthCancelled({
          message: messageText
        });
      };
      const handleAuthFailed = ({
        error,
        errorMessage,
        queryParams
      }) => {
        clearTimers();
        onAuthFailed === null || onAuthFailed === void 0 || onAuthFailed({
          error,
          errorMessage,
          queryParams
        });
      };
      const handleAuthSucceeded = authData => {
        clearTimers();
        onAuthSucceeded === null || onAuthSucceeded === void 0 || onAuthSucceeded(authData);
      };
      const handleAuth = ({
        code,
        error,
        errorMessage,
        queryParams
      }) => {
        if (error) {
          handleAuthFailed({
            error,
            errorMessage,
            queryParams
          });
        } else if (!code) {
          handleAuthCancelled({
            message: errorMessage
          });
        } else {
          handleAuthSucceeded(code);
        }
      };

      //@ts-expect-error authCallback does not exist on type 'Window & typeof globalThis'
      window.authCallback = handleAuth;
      //@ts-expect-error authCancelled does not exist on type 'Window & typeof globalThis'
      window.authCancelled = handleAuthCancelled;
      //@ts-expect-error authCancelled does not exist on type 'Window & typeof globalThis'
      window.authFailed = handleAuthFailed;
      popupRef.current = enviro.deployed() ? openPopup(popupWindowUrl) : openTab(popupWindowUrl);
      timeoutId = setTimeout(() => {
        handleAuthFailed({
          error: new Error('Reconnecting timeout'),
          errorMessage: I18n.text('reconnect.errors.reconnectFailedDueToTimeout')
        });
      }, timeout);
      intervalId = setInterval(() => {
        let popupIsClosed = !popupRef.current;
        try {
          /**
           * The workaround in case of Cross-Origin-Opener-Policy problem
           */
          popupIsClosed = popupRef.current.closed;
        } catch (err) {
          // do nothing
        }
        if (popupIsClosed) {
          handleAuthCancelled({});
        }
      }, DELAY_500_MS);
      if (!popupRef.current && authStartedRef.current) {
        onAuthFailed === null || onAuthFailed === void 0 || onAuthFailed({
          error: new Error('Unable to open popup window'),
          errorMessage: I18n.text('reconnect.errors.reconnectFailed')
        });
      }
    };
    if (popupUrl && !authStartedRef.current) {
      authStartedRef.current = true;
      openPopupWindow(popupUrl);
    }
  }, [popupUrl, onAuthFailed, onAuthSucceeded, onAuthCancelled, timeout]);
  return null;
};